import { Controller } from "@hotwired/stimulus"
import { turboFetch, escapeHtml } from "../util/common"

// Connects to the data-controller="search-select"
export default class extends Controller {
  static targets = [
    "input",
    "clearSearchButton",
    "results",
    "selectAllContainer",
    "selectAll",
    "selectAllLabel",
    "removeAllButton",
    "checkbox",
    "labelsContainer",
    "counterContainer",
    "counter",
    "label",
    "hiddenInput",
    "clearAllButton",
    "saveButton",
    "warning",
    "emptyPlaceholder",
    "filter",
    "appliedFiltersContainer",
    "clearAllFiltersButton"
  ]
  static values = {
    searchList: {
      type: Array,
      default: []
    },
    selectedIds: {
      type: Array,
      default: []
    },
    singleSelect: {
      type: Boolean,
      default: false
    },
    searchAllData: {
      type: Boolean,
      default: false
    },
    withDrawerSideCard: {
      type: Boolean,
      default: false
    },
    defaultAvatarUrl: String,
    trackChanges: {
      type: Boolean,
      default: false
    },
    simpleMode: {
      type: Boolean,
      default: false
    },
    canAdd: {
      type: Boolean,
      default: false
    },
    canRemove: {
      type: Boolean,
      default: false
    },
    canSelect: {
      type: Boolean,
      default: true
    },
    canCheck: {
      type: Boolean,
      default: true
    },
    canUncheck: {
      type: Boolean,
      default: true
    },
    noAddPermissionMessage: {
      type: String,
      default: "You don't have permission to add members"
    },
    noRemovePermissionMessage: {
      type: String,
      default: "You don't have permission to remove members"
    },
    defaultKey: {
      type: String,
      default: "full_name"
    },
    removeAllUrl: String,
    formObjectName: {
      type: String,
      default: "custom_badword"
    }
  }

  results = []
  selectedIds = []
  originalSelectedIds = []

  initialize() {
    this.search = this.search.bind(this)
    this.clearSearch = this.clearSearch.bind(this)
    this.toggleSelectAll = this.toggleSelectAll.bind(this)
    this.toggleSelectItem = this.toggleSelectItem.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.addNewItem = this.addNewItem.bind(this)
    this.removeItem = this.removeItem.bind(this)
  }

  connect() {
    this.inputTarget.addEventListener("input", this.search)
    this.inputTarget.addEventListener("keydown", this.handleKeydown)
    this.clearSearchButtonTarget.addEventListener("click", this.clearSearch)

    if (this.canSelectValue) {
      if (this.hasSelectAllTarget) {
        this.selectAllTarget.addEventListener("change", this.toggleSelectAll)
      }

      this.checkboxTargets.forEach(checkbox => checkbox.addEventListener("change", this.toggleSelectItem))
    }

    this.initSelected()
  }

  initSelected() {
    this.results = this.searchListValue
    this.selectedIds = this.selectedIdsValue.filter(id =>
      this.searchListValue.some(item => item.id === id)
    )

    this.originalSelectedIds = [...this.selectedIds]

    if (this.selectedIds.length) {
      const populateMethod = this.singleSelectValue
        ? this.populateSingleLabel.bind(this)
        : this.populateLabel.bind(this)
      this.selectedIds.forEach(id => populateMethod(id))
      if (this.hasCounterContainerTarget) {
        this.counterContainerTarget.classList.remove('hidden')
        this.updateCounter()
      }
    } else if (this.hasRemoveAllButtonTarget) {
      this.removeAllButtonTarget.classList.add('hidden')
    }

    this.updateHiddenInput()

    this.updateEmptyPlaceholder()
    this.updateSelectAllCheckedState()
    this.updateSaveButtonState()
    this.applyPermissionDisabledState()
  }

  applyPermissionDisabledState() {
    this.checkboxTargets.forEach(checkbox => {
      const id = +checkbox.value
      const isOriginallySelected = this.originalSelectedIds.includes(id)
      const label = checkbox.closest('label')

      if (isOriginallySelected && !this.canUncheckValue) {
        this.disableCheckboxWithTooltip(checkbox, label, this.noRemovePermissionMessageValue)
      }

      if (!isOriginallySelected && !this.canCheckValue) {
        this.disableCheckboxWithTooltip(checkbox, label, this.noAddPermissionMessageValue)
      }
    })
  }

  disableCheckboxWithTooltip(checkbox, label, message) {
    checkbox.disabled = true
    checkbox.title = message

    if (label) {
      label.title = message
      label.classList.add('opacity-60', 'cursor-not-allowed')
      label.classList.remove('cursor-pointer')
    }
  }

  search() {
    this.refreshResults()
  }

  applyFilter() {
    this.refreshResults()
  }

  refreshResults() {
    if (!this.searchListValue.length) return

    const query = this.inputTarget.value.toLowerCase().trim()
    const activeFilters = this.getActiveFilters()

    this.results = this.searchListValue.filter(item => {
      const passesFilters = activeFilters.every(
        ([dimension, values]) => this.matchesDimension(item, dimension, values)
      )
      if (!passesFilters) return false
      if (!query) return true

      return this.matchesQuery(item, query)
    })

    const hasResults = this.results.length > 0
    hasResults ? this.populateResults() : this.populateNoResults()

    this.updateSelectAllCheckedState()
    this.clearSearchButtonTarget.classList.toggle('hidden', !query)
    this.renderAppliedFilters(query, activeFilters)

    this.hideWarning()
  }

  renderAppliedFilters(query, activeFilters) {
    if (!this.hasAppliedFiltersContainerTarget) return

    this.appliedFiltersContainerTarget
      .querySelectorAll('[data-applied-filter-chip]')
      .forEach(chip => chip.remove())

    const chips = []
    if (query) chips.push(this.buildFilterChip('__search__', this.inputTarget.value.trim()))

    activeFilters.forEach(([dimension, values]) => {
      const select = this.filterTargets.find(sel => sel.dataset.filterDimension === dimension)
      values.forEach(value => {
        const option = select?.querySelector(`option[value="${CSS.escape(value)}"]`)
        const label = option?.textContent
        if (label) chips.push(this.buildFilterChip(dimension, label, value))
      })
    })

    chips.forEach(html => this.clearAllFiltersButtonTarget.insertAdjacentHTML('beforebegin', html))

    const hasChips = chips.length > 0
    this.appliedFiltersContainerTarget.classList.toggle('hidden', !hasChips)
    this.clearAllFiltersButtonTarget.classList.toggle('hidden', !hasChips)
  }

  buildFilterChip(key, label, value = '') {
    const valueAttr = value !== '' ? `data-value="${escapeHtml(value)}"` : ''
    return `
      <div class="filter-value flex items-center gap-4 text-sm" data-applied-filter-chip data-key="${escapeHtml(key)}" ${valueAttr}>
        <span class="whitespace-nowrap">${escapeHtml(label)}</span>
        <button type="button" class="flex-center p-4" data-action="click->search-select#clearFilter" data-key="${escapeHtml(key)}" ${valueAttr}>
          <i class="icon-xmark text-flameorange text-9"></i>
        </button>
      </div>
    `
  }

  clearFilter(event) {
    const key = event.currentTarget.dataset.key
    if (key === '__search__') {
      this.inputTarget.value = ''
    } else {
      const value = event.currentTarget.dataset.value
      this.removeFilterValue(key, value)
    }
    this.refreshResults()
  }

  removeFilterValue(dimension, value) {
    const select = this.filterTargets.find(sel => sel.dataset.filterDimension === dimension)
    if (!select) return
    if (select.tomselect) {
      select.tomselect.removeItem(value, true)
    } else {
      Array.from(select.options).forEach(option => {
        if (option.value === value) option.selected = false
      })
      select.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  clearAllFilters() {
    this.inputTarget.value = ''
    this.filterTargets.forEach(select => this.resetFilterSelect(select.dataset.filterDimension, select))
    this.refreshResults()
  }

  resetFilterSelect(dimension, select = null) {
    const target = select || this.filterTargets.find(sel => sel.dataset.filterDimension === dimension)
    if (!target) return
    if (target.tomselect) {
      target.tomselect.clear(true)
    } else {
      Array.from(target.options).forEach(option => { option.selected = false })
      target.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  getActiveFilters() {
    if (!this.hasFilterTarget) return []

    return this.filterTargets
      .map(select => [select.dataset.filterDimension, this.getSelectedValues(select)])
      .filter(([, values]) => values.length > 0)
  }

  getSelectedValues(select) {
    if (select.tomselect) {
      const raw = select.tomselect.getValue()
      return Array.isArray(raw) ? raw : (raw ? [raw] : [])
    }

    return Array.from(select.selectedOptions).map(option => option.value).filter(value => value !== "")
  }

  matchesDimension(item, dimension, selectedValues) {
    const selected = selectedValues.map(String)
    if (dimension === "nationality") {
      const itemIds = (item.nationalities || []).map(n => String(n.id))
      return itemIds.some(id => selected.includes(id))
    }
    return selected.includes(String(item[`${dimension}_id`]))
  }

  matchesQuery(item, query) {
    if (this.searchAllDataValue) {
      return Object.values(item).some(value => !!value?.toString().toLowerCase().includes(query))
    }

    const searchValue = item[this.defaultKeyValue]
    return !!searchValue && searchValue.toLowerCase().includes(query)
  }

  getSearchResults(query) {
    return this.searchAllDataValue
      ? this.searchListValue.filter(item => Object.values(item).some(value => !!value?.toString().toLowerCase().includes(query)))
      : this.searchListValue.filter(item => {
        const searchValue = item[this.defaultKeyValue]
        return searchValue && searchValue.toLowerCase().includes(query)
      })
  }

  clearSearch() {
    this.inputTarget.value = ""
    this.refreshResults()
    this.clearSearchButtonTarget.classList.add('hidden')
  }

  populateNoResults() {
    if (this.hasSelectAllContainerTarget) {
      this.selectAllContainerTarget.classList.add('hidden')
    }
    if (this.hasRemoveAllButtonTarget) {
      this.removeAllButtonTarget.classList.add('hidden')
    }

    this.resultsTarget.innerHTML = `<div class="text-center text-seasalt-500 py-20">No results found</div>`
  }

  populateResults() {
    this.resultsTarget.innerHTML = this.results.map(item => this.getResultItemHtml(item)).join("")

    if (this.canSelectValue) {
      Array.from(this.resultsTarget.children).forEach(child => child.addEventListener("change", this.toggleSelectItem))
      this.checkboxTargets.forEach(checkbox => checkbox.checked = this.selectedIds.includes(+checkbox.value))
      this.applyPermissionDisabledState()
    }

    if (this.canSelectValue && this.hasSelectAllContainerTarget) {
      this.selectAllContainerTarget.classList.remove('hidden')
    }
    if (this.hasRemoveAllButtonTarget) {
      this.removeAllButtonTarget.classList.remove('hidden')
    }
  }

  getResultItemHtml(item) {
    const flagHtml = item.country_code ? this.getFlagHtml(item.country_code.toLowerCase()) : ""

    const removeButtonHtml = this.canRemoveValue ? this.getRemoveButtonHtml(item) : ""

    return this.simpleModeValue
      ? this.getSimpleResultItemHtml(item, removeButtonHtml)
      : this.getDefaultItemHtml(item, flagHtml)
  }

  getFlagHtml(countryCode) {
    return `
      <div class="nationality-wrapper nationality-wrapper-sm">
        <img src="/icons/flags/${escapeHtml(countryCode)}.svg" class="size-full object-cover">
      </div>
    `
  }

  getRemoveButtonHtml(item) {
    return `
      <button class="flex-center p-4" data-id="${escapeHtml(item.id)}" data-action="click->search-select#removeItem">
        <i class="icon-xmark text-flameorange text-9"></i>
      </button>
    `
  }

  getSimpleResultItemHtml(item, removeButtonHtml) {
    return `
      <li class="flex items-center gap-10 pr-20 border-b border-seasalt-300">
        <label class="grow flex items-center gap-10 py-10 ${this.canSelectValue ? 'cursor-pointer' : ''}">
          ${this.getCheckboxHtml(item)}
          <span class="font-medium">${escapeHtml(item[this.defaultKeyValue])}</span>
        </label>
        ${removeButtonHtml}
      </li>
    `
  }

  getCheckboxHtml(item) {
    if (!this.canSelectValue) return ''

    return `
      <input type="checkbox"
        name=""
        value="${escapeHtml(item.id)}" class="checkbox checkbox--lg"
        ${this.selectedIds.includes(item.id) ? 'checked' : ''}
        data-search-select-target="checkbox">
    `
  }

  getDefaultItemHtml(item, flagHtml) {
    const itemJson = escapeHtml(JSON.stringify(item))
    const courseSubject = [item.course, item.subject].filter(Boolean).join(', ')
    const fullName = this.getTextValue(item[this.defaultKeyValue])
    const nationalityName = this.getTextValue(item.nationality_name)
    const roleText = this.getRoleText(item)
    const staffTypeBadgeHtml = item.staff_type_name
      ? `<span class="dialog-label">${escapeHtml(item.staff_type_name)}</span>`
      : ""

    return `
      <div class="flex items-center gap-10 pb-10 border-b border-seasalt-300">
        <label class="flex items-center gap-10 grow overflow-hidden ${this.canSelectValue ? 'cursor-pointer' : ''}"
          ${this.withDrawerSideCardValue
          ? `data-action="mouseenter->drawer-side-card#showCard mouseleave->drawer-side-card#hideCard" data-drawer-side-card-data="${itemJson}"`
          : ""
        }>
          ${this.getCheckboxHtml(item)}
          <div class="flex items-center gap-10 overflow-hidden">
            <div class="relative">
              <img src="${escapeHtml(item.avatar_url || this.defaultAvatarUrlValue)}" class="min-w-40 2xl:min-w-50 size-40 2xl:size-50 object-cover rounded-10 overflow-hidden">
              ${flagHtml}
            </div>
            <div class="flex flex-col gap-2 overflow-hidden">
              <div class="flex items-center gap-6">
                <span class="whitespace-nowrap">${escapeHtml(fullName)}</span>
                ${staffTypeBadgeHtml}
              </div>
              <div class="*:text-12 *:whitespace-nowrap *:truncate">
                <div title="${escapeHtml(nationalityName)}">${escapeHtml(nationalityName)}</div>
                <div title="${escapeHtml(courseSubject)}">${escapeHtml(courseSubject)}</div>
                <div title="${escapeHtml(roleText)}">${escapeHtml(roleText)}</div>
              </div>
            </div>
          </div>
        </label>
        ${item.row_action_html || ''}
      </div>
    `
  }

  getRoleText(item) {
    return item.role || ""
  }

  getTextValue(value) {
    return value || ""
  }

  clearAll() {
    this.checkboxTargets.forEach(checkbox => {
      if (checkbox.disabled) return
      checkbox.checked = false
    })

    this.labelTargets.forEach(label => {
      const id = +label.dataset.id
      const isOriginallySelected = this.originalSelectedIds.includes(id)
      if (isOriginallySelected && !this.canUncheckValue) return
      label.remove()
    })

    this.selectedIds = this.canUncheckValue ? [] : [...this.originalSelectedIds]

    this.updateSelectAllCheckedState()
    if (this.hasCounterContainerTarget) {
      this.updateCounter()
    }
    this.updateHiddenInput()
    this.updateSaveButtonState()
  }

  toggleSelectAll() {
    const isSelectAction = this.selectAllTarget.checked

    if (isSelectAction) {
      this.selectAllItems()
    } else {
      this.deselectAllItems()
    }

    this.updateSelectAllCheckedState()
    if (this.hasCounterContainerTarget) {
      this.counterContainerTarget.classList.toggle('hidden', !this.selectedIds.length)
      this.updateCounter()
    }
    this.updateHiddenInput()
    this.updateSaveButtonState()
  }

  selectAllItems() {
    this.resultsItemsIds.forEach(id => {
      const checkbox = this.checkboxTargets.find(cb => +cb.value === id)
      if (!checkbox || checkbox.disabled) return

      checkbox.checked = true

      if (!this.selectedIds.includes(id)) {
        this.selectedIds.push(id)
      }

      const isPopulated = this.labelTargets.some(label => +label.dataset.id === id)
      if (!isPopulated) {
        this.populateLabel(id)
      }
    })
  }

  deselectAllItems() {
    this.resultsItemsIds.forEach(id => {
      const checkbox = this.checkboxTargets.find(cb => +cb.value === id)
      if (!checkbox || checkbox.disabled) return

      checkbox.checked = false
      this.selectedIds = this.selectedIds.filter(selectedId => selectedId !== id)

      this.labelTargets.forEach(label => {
        if (+label.dataset.id === id) {
          label.remove()
        }
      })
    })
  }

  toggleSelectItem(event) {
    const id = event.target.value
    const isChecked = event.target.checked

    isChecked ? this.selectItem(id) : this.deselectItem(id)

    if (this.singleSelectValue) {
      this.checkboxTargets.forEach(checkbox => {
        if (checkbox.value !== id) {
          checkbox.checked = false
        }
      })
      this.selectedIds = isChecked ? [parseInt(id)] : []
    }

    if (!this.singleSelectValue) {
      this.updateSelectAllCheckedState()
    }

    if (this.hasCounterContainerTarget) {
      this.counterContainerTarget.classList.toggle('hidden', !this.labelTargets.length)
      this.updateCounter()
    }

    this.updateHiddenInput()
    this.updateSaveButtonState()
  }

  selectItem(id) {
    if (this.singleSelectValue) {
      this.populateSingleLabel(id)
      return
    }

    this.populateLabel(id)
    this.selectedIds.push(+id)
  }

  deselectItem(id) {
    this.removeLabel(id)
    this.selectedIds = this.selectedIds.filter(selectedId => selectedId !== +id)
  }

  populateLabel(id) {
    if (!this.hasLabelsContainerTarget) return

    const item = this.searchListValue.find(item => item.id === +id)
    const labelHtml = this.getLabelItemHtml(item)

    const labelElement = document.createRange().createContextualFragment(labelHtml)
    this.labelsContainerTarget.insertBefore(labelElement, this.clearAllButtonTarget)
  }

  populateSingleLabel(id) {
    const item = this.searchListValue.find(item => item.id === +id)
    const labelHtml = this.getLabelItemHtml(item)

    const labelElement = document.createRange().createContextualFragment(labelHtml)
    this.labelsContainerTarget.innerHTML = ''
    this.labelsContainerTarget.appendChild(labelElement)
  }

  getLabelItemHtml(item) {
    const isOriginallySelected = this.originalSelectedIds.includes(item.id)
    const canRemoveLabel = this.canUncheckValue || !isOriginallySelected

    const removeButtonHtml = canRemoveLabel
      ? `<i class="icon-xmark p-4 text-flameorange text-9 cursor-pointer" data-id="${escapeHtml(item.id)}" data-action="click->search-select#clearItem"></i>`
      : ""

    return `
      <div class="flex items-center gap-8 whitespace-nowrap" data-id="${escapeHtml(item.id)}" data-search-select-target="label">
        ${escapeHtml(item[this.defaultKeyValue])}
        ${removeButtonHtml}
      </div>
    `
  }

  clearItem(event) {
    const id = event.currentTarget.dataset.id

    const checkbox = this.checkboxTargets.find(checkbox => checkbox.value === id)
    if (checkbox) {
      checkbox.checked = false
    }

    this.removeLabel(id)

    this.updateSelectAllCheckedState()
    this.selectedIds = this.selectedIds.filter(selectedId => selectedId !== +id)
    this.updateCounter()
    this.updateHiddenInput()
    this.updateSaveButtonState()
  }

  updateSelectAllCheckedState() {
    if (!this.canSelectValue) return

    const checkboxes = this.checkboxTargets
    const checkedCheckboxes = checkboxes.filter(checkbox => checkbox.checked)
    if (this.hasSelectAllTarget) {
      const isAllChecked = checkboxes.length > 0 && checkboxes.length === checkedCheckboxes.length
      this.selectAllTarget.checked = isAllChecked
      this.updateSelectAllLabel(isAllChecked)
    }
  }

  updateSelectAllLabel(isAllChecked) {
    if (this.hasSelectAllLabelTarget) {
      this.selectAllLabelTarget.textContent = isAllChecked ? "Unselect all" : "Select all"
    }
  }

  updateCounter() {
    this.counterTarget.textContent = this.selectedIds.length
    this.counterContainerTarget.classList.toggle('hidden', !this.selectedIds.length)
  }

  removeLabel(id) {
    this.labelTargets.forEach(label => {
      if (label.dataset.id === id)
        label.remove()
    })
  }

  updateHiddenInput() {
    if (!this.hasHiddenInputTarget) return

    this.hiddenInputTarget.value = this.selectedIds.join(',')
  }

  get resultsItemsIds() {
    return this.results.map(item => item.id)
  }

  updateSaveButtonState() {
    if (!this.trackChangesValue) return
    if (!this.hasSaveButtonTarget) return

    this.saveButtonTarget.disabled = !this.hasChanges()
  }

  updateEmptyPlaceholder() {
    if (!this.hasEmptyPlaceholderTarget) return

    const isEmpty = !this.searchListValue.length
    this.emptyPlaceholderTarget.classList.toggle('hidden', !isEmpty)
  }

  hasChanges() {
    if (this.originalSelectedIds.length !== this.selectedIds.length) return true

    const sortedOriginal = [...this.originalSelectedIds].sort()
    const sortedCurrent = [...this.selectedIds].sort()

    return !sortedOriginal.every((id, index) => id === sortedCurrent[index])
  }

  handleKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()

      if (!this.canAddValue)
        return

      this.addNewItem()
    }
  }

  addNewItem() {
    const inputValue = this.inputTarget.value.trim()

    if (!inputValue) return

    const existingItem = this.searchListValue.find(item => {
      return item[this.defaultKeyValue]?.toLowerCase() === inputValue.toLowerCase()
    })

    if (existingItem) {
      this.showWarning("Already exists")
      return
    }

    this.hideWarning()

    this.inputTarget.value = ""
    this.populateResults()
    this.updateSelectAllCheckedState()
    this.updateHiddenInput()
    this.updateSaveButtonState()

    this.saveNewItem(inputValue)
    this.clearSearchButtonTarget.classList.add('hidden')
  }

  showWarning(message) {
    if (this.hasWarningTarget) {
      this.warningTarget.textContent = message
      this.warningTarget.classList.remove("hidden")
    }
  }

  hideWarning() {
    if (this.hasWarningTarget) {
      this.warningTarget.textContent = ""
      this.warningTarget.classList.add("hidden")
    }
  }

  saveNewItem(value) {
    const form = this.element.closest('form') || this.element.querySelector('form')

    if (!form) {
      this.showWarning('Configuration error')
      return
    }

    const params = {}
    params[this.formObjectNameValue] = { name: value }

    turboFetch(form.action, {
      method: 'POST',
      body: JSON.stringify(params)
    }).then(() => {
      this.updateEmptyPlaceholder()
    }).catch(error => {
      console.error('Error saving item:', error)
      this.showWarning('Failed to save item')
    })
  }

  removeItem(event) {
    event.preventDefault()
    event.stopPropagation()
    const id = event.currentTarget.dataset.id
    const item = this.searchListValue.find(item => item.id == id)

    if (!item || !item.url) {
      console.error('Item URL not found')
      this.showWarning('Configuration error')
      return
    }

    turboFetch(item.url, {
      method: 'DELETE'
    }).then(() => {
      this.updateEmptyPlaceholder()
    }).catch(error => {
      console.error('Error removing item:', error)
      this.showWarning('Failed to remove item')
    })
  }

  removeAll() {
    if (!this.removeAllUrlValue) {
      console.error('Remove all URL not configured')
      this.showWarning('Configuration error')
      return
    }

    turboFetch(this.removeAllUrlValue, {
      method: 'DELETE'
    }).then(() => {
      this.updateEmptyPlaceholder()
    }).catch(error => {
      console.error('Error removing all items:', error)
      this.showWarning('Failed to remove all items')
    })
  }

}
